import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {WellnessPlanDgSearchResults} from "../models/datagrid-search-results/wellness-plan.dg-search-results.model";
import {WellnessPlan} from "../models/wellness-plan.model";

/**
 * Serwis warstwy danych zarządzający szablonami Planów Abonamentowych (Wellness Plans).
 * Udostępnia pełen cykl operacji CRUD dla definicji abonamentów medycznych
 * tworzonych na poziomie klienta.
 */
@Injectable({
  providedIn: 'root'
})
export class WellnessPlanService implements DataGridServiceInterface<Observable<WellnessPlanDgSearchResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę zdefiniowanych szablonów planów opieki
   * na potrzeby widoku tabelarycznego (DataGrid).
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników względem początku zbioru.
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga decydująca o widoczności zarchiwizowanych planów.
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla tabeli.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<WellnessPlanDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/wellness-plans/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        const vetReservationsResults = new WellnessPlanDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new WellnessPlan(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
        return vetReservationsResults;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje szablony planów opieki w oparciu o zaawansowane filtry.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa parametrów wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Kolumna sortująca.
   * @param showDeletedObjects Flaga uwzględniająca usunięte plany.
   * @returns Observable emitujący przefiltrowaną listę szablonów.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<WellnessPlanDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/wellness-plans/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new WellnessPlanDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new WellnessPlan(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełne szczegóły wybranego szablonu planu opieki na podstawie jego UUID.
   * Służy m.in. do ładowania danych w formularzach edycji.
   *
   * @param uuid Unikalny identyfikator szablonu planu.
   * @returns Observable emitujący obiekt szczegółów planu.
   */
  getByUUID(uuid: string): Observable<WellnessPlan> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/wellness-plans/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<WellnessPlan>(new WellnessPlan(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Tworzy i zapisuje nową definicję/szablon planu opieki w systemie.
   *
   * @param wellnessPlan Obiekt reprezentujący nowy szablon.
   * @returns Observable emitujący zapisany obiekt wraz z nadanym przez backend identyfikatorem.
   */
  addWellnessPlan(wellnessPlan: WellnessPlan) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/wellness-plans', wellnessPlan).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<WellnessPlan>(new WellnessPlan(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje modyfikacje w istniejącym szablonie planu opieki.
   *
   * @param wellnessPlan Zmodyfikowany obiekt szablonu.
   * @returns Observable emitujący zaktualizowany obiekt lub błąd w przypadku niespełnienia walidacji.
   */
  updateWellnessPlan(wellnessPlan: WellnessPlan) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/wellness-plans', wellnessPlan).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<WellnessPlan>(new WellnessPlan(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Usuwa wskazany szablon planu opieki.
   *
   * @param uuid Identyfikator usuwanego szablonu.
   * @returns Observable emitujący usunięty obiekt lub status potwierdzenia.
   */
  deleteWellnessPlan(uuid: string) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/wellness-plans/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<WellnessPlan>(new WellnessPlan(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera wszystkie szablony planów zdefiniowane i udostępnione przez konkretnego klienta.
   *
   * @param uuid Identyfikator klienta.
   * @returns Observable emitujący tablicę szablonów planów przypisanych do sieci.
   */
  getByClientUUID(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/wellness-plans/clients/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new WellnessPlan(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
